import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../utils/api'; // Reverted import to original pattern
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
            try {
                const decodedToken = jwtDecode(storedToken);
                if (decodedToken.exp * 1000 > Date.now()) {
                    api.default.defaults.headers.common['x-auth-token'] = storedToken;
                    return storedToken;
                } else {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    return null;
                }
            } catch (error) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                return null;
            }
        }
        return null;
    });

    const [user, setUser] = useState(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser && localStorage.getItem('token')) {
            try {
                return JSON.parse(storedUser);
            } catch (error) {
                return null;
            }
        }
        return null;
    });

    const [loading, setLoading] = useState(true); // Always start in loading state to verify status
    
    const navigate = useNavigate();

    const logout = useCallback(async () => {
        console.log('AuthContext: Performing local logout.'); // Log logout
        try {
            await api.logout(); // Use api.logout()
        } catch (error) {
            console.error('AuthContext: Error during logout API call:', error);
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete api.default.defaults.headers.common['x-auth-token']; // Use api.default
        setToken(null);
        setUser(null);
        navigate('/landing', { state: { openLogin: true } });
    }, [navigate]);

    const refreshUser = useCallback(async (currentToken) => {
        const tokenToUse = currentToken || token || localStorage.getItem('token');
        console.log('AuthContext: refreshUser called with token:', tokenToUse ? 'present' : 'absent');
        try {
            if (tokenToUse) {
                api.default.defaults.headers.common['x-auth-token'] = tokenToUse; // Use api.default
            } else {
                console.log('AuthContext: No token provided to refreshUser, logging out.');
                logout(); // If no token, log out
                return null;
            }
            const { data } = await api.getUser(); // Use api.getUser()
            console.log('AuthContext: api.getUser() successful, user data received:', data);
            setUser(data);
            localStorage.setItem('user', JSON.stringify(data));
            return data;
        } catch (error) {
            console.error('AuthContext: Error refreshing user (api.getUser() failed):', error);
            // If it's a 401, we should logout. Other errors might be temporary.
            if (error.response && error.response.status === 401) {
                logout();
            }
            return null;
        }
    }, [token, logout]);

    useEffect(() => {
        const initializeAuth = async () => {
            console.log('AuthContext: initializeAuth started.');
            if (token) {
                // Token already validated synchronously in useState init
                api.default.defaults.headers.common['x-auth-token'] = token;
                // Refresh user in background
                await refreshUser(token);
            }
            setLoading(false);
        };
        initializeAuth();
    }, [refreshUser, token]); // Added refreshUser and token

    const login = useCallback(async (emailOrMobile, password) => {
        setLoading(true);
        try {
            console.log('AuthContext: Calling api.login...');
            const { data } = await api.login(emailOrMobile, password); // Use api.login()
            console.log('AuthContext: api.login() successful, response received.');
            const { token, user } = data;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            api.default.defaults.headers.common['x-auth-token'] = token; // Use api.default
            setToken(token);
            setUser(user);
            return { user };
        } catch (error) { // Explicit catch for AuthContext's login
            console.error('AuthContext: Error during login:', error); // New log
            throw error; // Re-throw to propagate to LoginPage's catch
        } finally {
            setLoading(false);
        }
    }, []);

    const register = useCallback(async (userData) => {
        setLoading(true);
        try {
            console.log('AuthContext: Calling api.register...');
            const { data } = await api.register(userData); // Use api.register()
            console.log('AuthContext: api.register() successful, response received.');
            const { token, user } = data;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            api.default.defaults.headers.common['x-auth-token'] = token; // Use api.default
            setToken(token);
            setUser(user);
            return user;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateUser = useCallback((updatedUser) => {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
    }, []);

    const updateUserChartSettings = useCallback(async (settings) => {
        try {
            await api.updateChartSettings(settings); // Use api.updateChartSettings()
        } catch (error) {
            console.error('AuthContext: Error updating chart settings:', error);
        }
    }, []);

    const updateUserCardSymbol = useCallback(async (cardSymbol) => {
        try {
            const res = await api.updateCardSymbol(cardSymbol); // Use api.updateCardSymbol()
            setUser(prevUser => ({ ...prevUser, cardSymbol: res.data.cardSymbol }));
            localStorage.setItem('user', JSON.stringify({ ...user, cardSymbol: res.data.cardSymbol }));
        } catch (error) {
            console.error('AuthContext: Error updating user card symbol:', error);
        }
    }, [user]);

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        updateUser,
        refreshUser,
        updateUserChartSettings,
        updateUserCardSymbol,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};